import { useEffect, useState } from "react";
import { useLiveEvents } from "@/lib/liveEvents";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HealthScoreRing from "@/components/HealthScoreRing";
import FoundingBanner from "@/components/FoundingBanner";
import MetadataAuthorityCard from "@/components/MetadataAuthorityCard";
import RoyaltyInfrastructureCard from "@/components/RoyaltyInfrastructureCard";
import RoadmapCard from "@/components/RoadmapCard";
import PassportCard from "@/components/PassportCard";
import DiscographyCard from "@/components/DiscographyCard";
import VerifiedPortfolio from "@/components/VerifiedPortfolio";
import AchievementVault from "@/components/AchievementVault";
import MembershipCheckout from "@/components/MembershipCheckout";
import OnboardingTour from "@/components/OnboardingTour";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import { AlertTriangle, CheckCircle2, Lock, ArrowRight, ListChecks, Crown, Info, Gauge, IdCard, BarChart3, Trophy, GraduationCap, ChevronDown } from "lucide-react";

const DEFAULT_STATUS = { key: "risk", label: "Producer Risk", color: "#E11D48" };

const STATUS_TIPS = {
  risk: "Significant exposure. Prioritize the action plan below immediately.",
  developing: "You're building. Focus on registrations and metadata authority.",
  professional: "Solid foundation. Push into Industry Ready by closing the gaps below.",
  industry: "Industry ready. Maintain, then push for Elite Infrastructure.",
  elite: "Elite Infrastructure — best in class. Defend the position.",
};

/* Collapsible section — click header (icon + numbered title) to expand/collapse.
 * State persists per user in localStorage. Users can also jump to any section
 * via the icon tab bar at the top of the dashboard. */
function CollapsibleSection({ id, number, title, icon: Icon, accent = "#D4AF37", defaultOpen = true, visible = true, children }) {
  const storageKey = `bc_dash_section_${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? defaultOpen : v === "1";
    } catch { return defaultOpen; }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, open ? "1" : "0"); } catch { /* noop */ }
  }, [open, storageKey]);

  // Icon-grid launcher toggles the section's visibility outright — the
  // whole band + body is skipped when the tile isn't active.
  if (!visible) return null;

  return (
    <section
      id={`section-${id}`}
      data-testid={`section-${id}`}
      className="scroll-mt-24"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`section-body-${id}`}
        data-testid={`toggle-section-${id}`}
        className="w-full my-4 flex items-center gap-4 select-none group text-left hover:opacity-90 transition-opacity"
      >
        <span
          className="w-9 h-9 rounded-full border flex items-center justify-center shrink-0 transition-colors"
          style={{
            borderColor: `${accent}55`,
            background: open ? `${accent}18` : `${accent}08`,
            boxShadow: open ? `0 0 14px ${accent}66` : "none",
          }}
        >
          {Icon && <Icon size={16} style={{ color: accent }} strokeWidth={1.8} />}
        </span>
        <span
          className="font-mono-tech text-sm sm:text-base uppercase tracking-[0.2em] font-semibold shrink-0"
          style={{ color: accent }}
        >
          {String(number).padStart(2, "0")} — {title}
        </span>
        <span
          className="flex-1 h-px"
          style={{ background: `linear-gradient(to right, ${accent}55, transparent)` }}
        />
        <ChevronDown
          size={18}
          style={{ color: accent }}
          className={`transition-transform duration-300 shrink-0 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div id={`section-body-${id}`} className="animate-in fade-in duration-200">
          {children}
        </div>
      )}
    </section>
  );
}

/* Icon tab bar — sticky at the top of the dashboard so users can jump directly
 * to any section. Click a tab and it scrolls (with a smooth animation) to that
 * section and expands it if it was collapsed. */
/* Icon-Grid Launcher — luxury app-launcher style navigation that replaces
 * the horizontal tab bar. Each tile is a large tap-friendly card with an
 * accent-colored lucide icon, section title, and short caption. Clicking a
 * tile smoothly opens/scrolls to its section (same behavior as before).
 * Grid is responsive: 2 cols mobile → 4 cols desktop. */
function SectionTabBar({ sections, activeId, onSelect, isUnlocked }) {
  return (
    <nav
      data-testid="section-tabbar"
      aria-label="Dashboard sections"
      className="mb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-up"
    >
      {sections.map((s) => {
        const active = activeId === s.id;
        const Icon = s.icon;
        const unlocked = isUnlocked ? isUnlocked(s.id) : true;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onSelect(s.id)}
            data-testid={`tab-${s.id}`}
            title={s.title}
            className={`group relative text-left transition-all duration-200 border overflow-hidden ${
              active
                ? "bg-[#0f0f0f] border-transparent -translate-y-0.5"
                : "bg-[#0a0a0a] border-white/5 hover:bg-[#0f0f0f] hover:-translate-y-0.5"
            }`}
            style={
              active
                ? {
                    borderColor: `${s.accent}66`,
                    boxShadow: `inset 0 0 0 1px ${s.accent}33, 0 20px 40px -20px ${s.accent}aa`,
                  }
                : {}
            }
          >
            {/* Top accent bar */}
            <span
              className="absolute top-0 inset-x-0 h-[3px] transition-opacity"
              style={{ background: s.accent, opacity: active ? 1 : 0.7 }}
            />
            {/* Lock badge — corner marker for tiles the user's tier
                can't access. Clicking still triggers onSelect, which
                will route them to /plans. */}
            {!unlocked && (
              <span
                data-testid={`tab-${s.id}-lock`}
                className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center border border-amber-500/40 bg-black/70"
              >
                <Lock size={12} className="text-amber-500" strokeWidth={2} />
              </span>
            )}
            {/* Live activity badge — top-right corner, only when badge > 0 */}
            {typeof s.badge === "number" && s.badge > 0 && (
              <span
                data-testid={`tab-${s.id}-badge`}
                className="absolute top-3 right-3 min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full text-[10px] font-mono-tech font-bold z-10"
                style={{
                  background: s.accent,
                  color: "#000",
                  boxShadow: `0 0 12px ${s.accent}88`,
                }}
              >
                {s.badge > 99 ? "99+" : s.badge}
              </span>
            )}
            <div className="p-6">
              {/* Header row: Category label + icon */}
              <div className="flex items-start justify-between gap-4 mb-6">
                <div
                  className="text-[10px] font-mono-tech uppercase tracking-[0.22em] leading-relaxed"
                  style={{ color: s.accent }}
                >
                  {s.category || s.title}
                </div>
                <div
                  className="w-9 h-9 rounded-full border flex items-center justify-center shrink-0 transition-transform group-hover:scale-105"
                  style={{
                    borderColor: `${s.accent}55`,
                    background: active ? `${s.accent}18` : `${s.accent}08`,
                    boxShadow: active ? `0 0 16px ${s.accent}66` : "none",
                  }}
                >
                  <Icon size={16} strokeWidth={1.6} style={{ color: s.accent }} />
                </div>
              </div>
              {/* Feature title */}
              <h3 className="font-display text-2xl leading-tight text-white mb-3">
                {s.title}
              </h3>
              {/* Abbreviation pill */}
              <div
                className="inline-flex items-center px-2 py-0.5 border mb-4"
                style={{
                  borderColor: `${s.accent}55`,
                  background: `${s.accent}10`,
                }}
              >
                <span
                  className="text-[10px] font-mono-tech font-semibold tracking-[0.22em]"
                  style={{ color: s.accent }}
                >
                  {s.short}
                </span>
              </div>
              {/* Description */}
              {s.desc && (
                <p className="text-xs text-neutral-400 leading-relaxed">
                  {s.desc}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </nav>
  );
}

/* Visual section divider — kept for backwards-compatible import points; new
 * usages should go through <CollapsibleSection>. */
function SectionBand({ number, title, accent = "#D4AF37" }) {
  return (
    <div
      data-testid={`section-band-${number}`}
      className="relative my-8 first:mt-0 flex items-center gap-4 select-none"
    >
      <span
        className="w-3 h-3 rounded-full shrink-0"
        style={{ background: accent, boxShadow: `0 0 14px ${accent}99` }}
      />
      <span
        className="font-mono-tech text-sm sm:text-base uppercase tracking-[0.2em] shrink-0 font-semibold"
        style={{ color: accent }}
      >
        {String(number).padStart(2, "0")} — {title}
      </span>
      <span
        className="flex-1 h-px"
        style={{ background: `linear-gradient(to right, ${accent}55, transparent)` }}
      />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const [d, a] = await Promise.all([
        api.get("/dashboard"),
        api.get("/achievements").catch(() => ({ data: { achievements: [] } })),
      ]);
      setData(d.data);
      setAchievements(a.data.achievements || []);
    } catch (err) { setError(formatApiError(err)); }
  };

  useEffect(() => { refresh(); }, [location.state]);

  // Icon-grid launcher — each tile toggles a single section on/off.
  // Sections stay hidden until a tile is clicked.
  const [activeSection, setActiveSection] = useState(null);
  const [dashAccess, setDashAccess] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/royai/access-status");
        setDashAccess(data);
      } catch { /* treat as guest */ }
    })();
  }, []);
  // Map Dashboard section id → feature-key used by the access-status API.
  const SECTION_FEATURE = { 1: "MCS", 2: "PASSPORT", 3: "SBD", 5: "VAULT", 6: "ROADMAP", 7: "GAPS", 8: "PORTFOLIO", 9: null };
  const isSectionUnlocked = (id) => {
    const key = SECTION_FEATURE[id];
    if (!key) return true; // Section 9 (Unlock CTA) is always visible.
    const allowed = dashAccess?.allowed_features || [];
    return allowed.includes(key);
  };
  const selectSection = (id) => {
    if (!isSectionUnlocked(id)) {
      // Locked tile → send user straight to the pricing page.
      window.location.href = "/plans";
      return;
    }
    setActiveSection((prev) => (prev === id ? null : id));
  };

  // Live push: when admin approves evidence, the backend fires
  // `evidence.approved` on this user's SSE channel — refetch instantly so
  // the score, verified badges, and recoverable totals reflect the change
  // without a page reload.
  useLiveEvents(["evidence.approved", "audit.updated", "score.updated"], () => {
    refresh();
    const t = document.getElementById("bc-live-toast-anchor");
    if (t) t.dispatchEvent(new CustomEvent("bc-live-update"));
  });

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar showSearch />
        <div className="p-12 text-rose-400">{error}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar showSearch />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-overline">Loading dashboard…</div>
        </div>
      </div>
    );
  }

  const a = data.assessment;
  const isMember = data.membership?.active;
  const score = a?.score ?? null;
  const status = a?.status || DEFAULT_STATUS;
  const tip = STATUS_TIPS[status.key] || "";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar showSearch />

      <div className="px-6 md:px-10 lg:px-16 py-10 lg:py-14">
        <VerifyEmailBanner />
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-overline">BeatCredsi<span className="text-amber-500">™</span> Dashboard</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mt-2">
              Welcome back, <span className="text-amber-500">{user?.name?.split(" ")[0] || "Creator"}.</span>
            </h1>
          </div>
          <div className="flex items-center gap-3" data-testid="membership-status">
            <span className={`text-overline ${isMember ? "text-emerald-400" : "text-neutral-500"}`}>
              {isMember ? "Member Active" : "Free Tier"}
            </span>
            <span className={`w-2 h-2 ${isMember ? "bg-emerald-400" : "bg-amber-500"}`} />
          </div>
        </div>

        {/* WHAT IS BEATCREDSI — collapsible-style info card (when no score yet) */}
        {!a && (
          <div className="mb-10 p-6 lg:p-8 border border-amber-500/20 bg-amber-500/5" data-testid="what-is-beatcredsi-card">
            <div className="flex items-start gap-4">
              <Info className="text-amber-500 shrink-0 mt-1" size={20} />
              <div>
                <div className="text-overline mb-2">What is BeatCredsi?</div>
                <p className="text-sm text-neutral-200 leading-relaxed max-w-3xl">
                  BeatCredsi<span className="text-amber-500">™</span> is the world&apos;s first <strong className="text-white">Music Creator Infrastructure Scoring System</strong>. Just as a credit score measures financial health, BeatCredsi measures music-creator infrastructure health — copyright protection, royalty registrations, metadata authority, catalog operations, and business structure. Take the assessment to get your score from 0 to 1000.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* SECTION TAB BAR — icon buttons act as toggles for each section */}
        <SectionTabBar
          activeId={activeSection}
          onSelect={selectSection}
          isUnlocked={isSectionUnlocked}
          sections={[
            { id: 1, number: 1, short: "SCORE",       title: "Music Creator Score",  fn: "Your 0–1000 infrastructure health rating",     icon: Gauge,          accent: "#D4AF37" },
            { id: 2, number: 2, short: "PASSPORT",    title: "BeatCredsi Passport",  fn: "Build your verified public profile, edited live",         icon: IdCard,         accent: "#10B981" },
            ...(a && a.categories ? [{ id: 3, number: 3, short: "BREAKDOWN", title: "Score Breakdown",  fn: "Per-category strengths + weaknesses",           icon: BarChart3,     accent: "#38BDF8" }] : []),
            { id: 5, number: 5, short: "VAULT",       title: "Achievement Vault",    fn: "Certifications, plaques and press evidence",     icon: Trophy,         accent: "#A78BFA" },
            { id: 6, number: 6, short: "ROADMAP",     title: "Learning Roadmap",     fn: "Structured courses to raise your score",         icon: GraduationCap, accent: "#38BDF8" },
            ...(a ? [{ id: 7, number: 7, short: "GAPS",     title: "Gaps & Next Moves",    fn: "Prioritized fixes that move the needle",         icon: AlertTriangle, accent: "#E11D48" }] : []),
            { id: 8, number: 8, short: "PORTFOLIO", title: "Verified Portfolio",   fn: "Every finalized track with evidence coverage",    icon: Trophy,        accent: "#D4AF37" },
          ]}
        />

        {/* 01 — MUSIC CREATOR SCORE */}
        <CollapsibleSection visible={activeSection === 1} id={1} number={1} title="Music Creator Score" icon={Gauge} accent="#D4AF37">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/5 border border-white/5 mb-px">
          <div className="lg:col-span-5 bg-[#0c0c0c] p-8 flex items-center gap-8" data-testid="dashboard-score-card">
            {score === null ? (
              <div>
                <div className="text-overline mb-2">No Score Yet</div>
                <h2 className="font-display text-2xl mb-3">Get your BeatCredsi Score.</h2>
                <p className="text-sm text-neutral-400 mb-4">22 questions. One number. Your music-creator status.</p>
                <Link to="/assessment" data-testid="start-assessment-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-amber-500 text-black font-semibold hover:brightness-110 transition-all">
                  Get My BeatCredsi Score <ArrowRight size={16} />
                </Link>
              </div>
            ) : (
              <>
                <HealthScoreRing score={score} max={a.score_max || 1000} status={status} size={200} />
                <div>
                  <div className="text-overline mb-2">BeatCredsi Score<span className="text-amber-500">™</span></div>
                  <div className="font-mono-tech text-xs text-neutral-500 mb-3">Music Infrastructure Intelligence</div>
                  <Link to="/assessment" data-testid="retake-assessment-cta"
                    className="inline-flex items-center gap-2 text-sm text-amber-500 hover:underline">
                    Retake assessment →
                  </Link>
                </div>
              </>
            )}
          </div>

          <div className="lg:col-span-4 bg-[#0c0c0c] p-8" data-testid="dashboard-status-card">
            <div className="text-overline mb-3">Creator Status</div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-3 h-3" style={{ background: status.color }} />
              <div className="font-display text-2xl lg:text-3xl font-medium" style={{ color: status.color }}>{status.label}</div>
            </div>
            <div className="text-overline mb-3 text-neutral-400">Risk: {a?.risk_rating || "—"}</div>
            <p className="text-sm text-neutral-400">{tip}</p>
          </div>

          <div className="lg:col-span-3 bg-[#0c0c0c] p-8" data-testid="dashboard-membership-card">
            <div className="text-overline mb-3">Membership</div>
            {isMember ? (
              <>
                <div className="font-display text-xl mb-1">{data.membership.plan}</div>
                {data.membership.is_founding && (
                  <span data-testid="founding-badge" className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-500/15 border border-amber-500/30 mb-4">
                    <Crown size={11} className="text-amber-500" />
                    <span className="text-xs uppercase tracking-widest font-semibold text-amber-500">Founding · #{data.membership.founding_member_number}</span>
                  </span>
                )}
                <div className="inline-flex items-center gap-2 text-sm text-emerald-400 mt-1">
                  <CheckCircle2 size={16} /> Active subscription
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-neutral-400 mb-4 leading-relaxed">
                  Unlock the full BeatCredsi platform — Passport, Achievement Vault,
                  Learning Roadmap, and the RoyAI Intelligence Suite.
                </p>
                <Link
                  to="/plans"
                  data-testid="upgrade-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-black font-semibold text-sm hover:brightness-110 transition-all"
                  style={{ boxShadow: "0 10px 30px -12px #D4AF37cc" }}
                >
                  See Membership Plans <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        </div>
        </CollapsibleSection>

        {/* 02 — PUBLIC PROFILE */}
        <CollapsibleSection visible={activeSection === 2} id={2} number={2} title="Public Profile" icon={IdCard} accent="#10B981">
        <div className="border border-white/5 border-t-0 mb-px">
          <PassportCard hasAssessment={!!a} />
        </div>

        <div className="border border-white/5 border-t-0 mb-px">
          <DiscographyCard />
        </div>
        </CollapsibleSection>

        {a && a.categories && (
          <CollapsibleSection visible={activeSection === 3} id={3} number={3} title="Score Breakdown" icon={BarChart3} accent="#38BDF8">
            <div className="border border-white/5 border-t-0 mb-px" data-testid="category-breakdown">
            <div className="p-8 bg-[#0c0c0c]">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div className="text-overline">Score Categories</div>
                <div className="font-mono-tech text-xs text-neutral-500">5 pillars · weighted contribution</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5">
                {a.categories.map((c) => (
                  <div key={c.id} className="bg-[#0A0A0A] p-5 flex flex-col" data-testid={`category-${c.id}`}>
                    <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{c.label}</div>
                    <div className="font-display text-3xl font-medium" style={{ color: c.status.color }}>{c.score}</div>
                    <div className="font-mono-tech text-xs text-neutral-500 mb-3">/ 1000</div>
                    <div className="h-1 w-full bg-white/5 mt-auto">
                      <div className="h-1" style={{ background: c.status.color, width: `${Math.max(2, (c.score / 1000) * 100)}%` }} />
                    </div>
                    <div className="text-[13px] uppercase tracking-widest font-semibold mt-2" style={{ color: c.status.color }}>{c.status.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {a && <MetadataAuthorityCard assessment={a} onUpdate={refresh} />}
          {a && <RoyaltyInfrastructureCard assessment={a} onUpdate={refresh} />}
          </CollapsibleSection>
        )}

        {/* 05 — ACHIEVEMENT VAULT */}
        <CollapsibleSection visible={activeSection === 5} id={5} number={5} title="Achievement Vault" icon={Trophy} accent="#A78BFA">
          <div className="mt-px">
            <AchievementVault achievements={achievements} isOwner ownerName={user?.name} />
          </div>
        </CollapsibleSection>

        {/* 06 — LEARNING ROADMAP */}
        <CollapsibleSection visible={activeSection === 6} id={6} number={6} title="Learning Roadmap" icon={GraduationCap} accent="#38BDF8">
          <div className="mt-px">
            <RoadmapCard />
          </div>
        </CollapsibleSection>

        {/* 07 — GAPS & NEXT MOVES */}
        {a && (
          <CollapsibleSection visible={activeSection === 7} id={7} number={7} title="Gaps & Next Moves" icon={AlertTriangle} accent="#E11D48">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/5 border border-white/5 mb-12 mt-px">
            <div className="bg-[#0c0c0c] p-8" data-testid="missing-items-card">
              <div className="flex items-center gap-2 mb-6">
                <AlertTriangle size={16} className="text-amber-500" />
                <div className="text-overline">Missing Items</div>
                <span className="ml-auto font-mono-tech text-xs text-neutral-500">{a.missing_items.length} flagged</span>
              </div>
              {a.missing_items.length === 0 ? (
                <p className="text-sm text-neutral-400">No gaps detected. You&apos;re operating clean.</p>
              ) : (
                <ul className="space-y-3">
                  {a.missing_items.map((m, i) => (
                    <li key={m.id} className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0" data-testid={`missing-item-${m.id}`}>
                      <span className="font-mono-tech text-xs text-neutral-500 mt-1">{String(i + 1).padStart(2, "0")}</span>
                      <div className="flex-1">
                        <div className="text-sm text-white">{m.label}</div>
                        {m.course && <div className="text-xs text-neutral-500 mt-1">→ {m.course}</div>}
                      </div>
                      <span className={`text-xs px-2 py-0.5 uppercase tracking-widest font-semibold
                        ${m.severity === "high" ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-400"}`}>
                        {m.severity}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-[#0c0c0c] p-8" data-testid="action-plan-card">
              <div className="flex items-center gap-2 mb-6">
                <ListChecks size={16} className="text-amber-500" />
                <div className="text-overline">Recommended Action Plan</div>
              </div>
              {a.action_plan.length === 0 ? (
                <p className="text-sm text-neutral-400">No actions needed. Keep shipping.</p>
              ) : (
                <ol className="space-y-4">
                  {a.action_plan.map((s) => (
                    <li key={s.step} className="flex gap-4" data-testid={`action-step-${s.step}`}>
                      <div className="font-display text-xl text-amber-500 w-8 shrink-0">{String(s.step).padStart(2, "0")}</div>
                      <div>
                        <div className="text-sm text-white">{s.title}</div>
                        {s.course && <div className="text-xs text-neutral-500 mt-1">Course: {s.course}</div>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
          </CollapsibleSection>
        )}

        {/* 08 — VERIFIED PORTFOLIO (Receipt of your audited discography) */}
        <CollapsibleSection visible={activeSection === 8} id={8} number={8} title="Verified Portfolio" icon={Trophy} accent="#D4AF37">
          <VerifiedPortfolio />
        </CollapsibleSection>

        {/* Section 09 removed — pricing now lives on the dedicated /plans page. */}

        {/* COURSES section is now rendered as the BeatCredsi Academy via RoadmapCard below */}

        <footer className="mt-16 pt-8 border-t border-white/5 text-xs text-neutral-500 flex flex-wrap items-center justify-between gap-3">
          <span>Built by Smartian Tech<span className="text-amber-500">™</span> · © 2026 Only Few Get Selected, LLC</span>
          <span className="flex items-center gap-4">
            <Link to="/account" className="hover:text-amber-500" data-testid="dashboard-account-link">Account</Link>
            <Link to="/terms" className="hover:text-amber-500">Terms</Link>
            <Link to="/privacy" className="hover:text-amber-500">Privacy</Link>
          </span>
        </footer>
      </div>
      <OnboardingTour assessment={a} />
    </div>
  );
}
